import {
  AdvertisementPortalIdEnum,
  CarFileMarktplaatsStatusEnum,
} from 'config/enums'
import propTypes from 'prop-types'
import React from 'react'
import styled from 'styled-components/macro'
import Icon from 'components/atoms/icon'
import Typography from 'components/molecules/typography'
import Button from 'components/atoms/button'
import { formatDateTillNowInDays } from 'utilities/format'
import { publishPortal } from 'redux/actions/data'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

const StyledMarktplaatsCarCard = styled.article`
  background: ${({ theme }) => theme.colors.fieldBackground};
  padding: ${({ theme, $iconOnly }) =>
    $iconOnly ? 0 : theme.sizeByFactor(1.5)};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  display: flex;
  align-items: center;

  p {
    margin: 0;
  }

  span {
    box-sizing: initial;
    padding-right: ${({ theme }) => theme.sizings.lvl0};

    path:first-child {
      fill: ${({ theme, $inactive }) => $inactive && theme.colors.errorIcon};
    }
  }

  .divider {
    width: 1px;
    height: 100%;
    background: ${({ theme }) => theme.colors.inactiveBackground};
    margin: 0 ${({ theme }) => theme.sizings.lvl1};
  }
`

const StyledMarktplaatsNotPublished = styled.div`
  display: flex;
  flex-direction: ${({ $listView }) => ($listView ? 'column' : 'row')};
  width: 100%;
  height: 100%;
  align-items: ${({ $listView }) => ($listView ? 'start' : 'center')};
  justify-content: ${({ $listView }) => ($listView ? 'center' : 'start')};
  font-weight: ${({ theme }) => theme.baseFontWeight};
  padding: 0 ${({ theme }) => theme.sizings.lvl0};
`

const StyledMarktplaatsPublished = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  padding-left: ${({ theme }) => theme.sizeByFactor(1)};

  span {
    padding-right: ${({ theme }) => theme.lvl0};
  }
`

const StyledMarktplaatsInfo = styled.div`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.smallFontSize};
`

function MarktplaatsCarCard({
  className,
  status,
  iconOnly,
  onClick,
  marktplaatsData,
  carId,
  onMarktplaatsStatusChange,
  listView,
}) {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const handlePublishCar = async () => {
    await dispatch(
      publishPortal('portalPublish', {
        auto_id: Number(carId),
        portal_ids: [Number(AdvertisementPortalIdEnum.MARKTPLAATS)],
      }),
    )
    onMarktplaatsStatusChange(CarFileMarktplaatsStatusEnum.PENDING) // Call the callback with the new status
    return true
  }

  if (iconOnly) {
    return (
      <StyledMarktplaatsCarCard
        className={className}
        $inactive={status !== CarFileMarktplaatsStatusEnum.PUBLISHED}
        $clickable
        onClick={onClick}
        $iconOnly={iconOnly}
      >
        <Icon size="md" type="marktplaats" />
      </StyledMarktplaatsCarCard>
    )
  } else if (status === CarFileMarktplaatsStatusEnum.ERROR) {
    return (
      <StyledMarktplaatsCarCard className={className} $inactive>
        <Icon size="lg" type="marktplaats" />
        <Typography type="BodyParagraph">
          {t('marktplaatsView.status.error')}
        </Typography>
      </StyledMarktplaatsCarCard>
    )
  } else if (status === CarFileMarktplaatsStatusEnum.PUBLISHED) {
    return (
      <StyledMarktplaatsCarCard
        onClick={onClick}
        className={className}
        $clickable
      >
        <Icon size="lg" type="marktplaats" />
        <StyledMarktplaatsPublished $listView={listView}>
          {marktplaatsData?.expires_at && (
            <>
              <StyledMarktplaatsInfo>
                <Icon size="md" type="accessTime" />
                <Typography type="BodyParagraph">
                  {formatDateTillNowInDays(marktplaatsData?.expires_at)}
                  {t('dys').toLocaleLowerCase()}
                </Typography>
              </StyledMarktplaatsInfo>
              {!listView && <div className="divider" />}
            </>
          )}
          <StyledMarktplaatsInfo>
            <Icon size="md" type="search" />
            <Typography type="BodyParagraph">
              {marktplaatsData?.clicks}
            </Typography>
          </StyledMarktplaatsInfo>
          <div className="divider" />
          <StyledMarktplaatsInfo>
            <Icon size="md" type="views" />
            <Typography type="BodyParagraph">
              {marktplaatsData?.views}
            </Typography>
          </StyledMarktplaatsInfo>
        </StyledMarktplaatsPublished>
      </StyledMarktplaatsCarCard>
    )
  } else if (status === CarFileMarktplaatsStatusEnum.PENDING) {
    return (
      <StyledMarktplaatsCarCard className={className}>
        <Icon size="lg" type="marktplaats" />
        <Typography type="BodyParagraph">
          {t('marktplaatsView.status.pending')}
        </Typography>
      </StyledMarktplaatsCarCard>
    )
  } else {
    return (
      <StyledMarktplaatsCarCard className={className} $inactive>
        <Icon size="lg" type="marktplaats" />
        <StyledMarktplaatsNotPublished $listView={listView}>
          <Typography type="BodyParagraph">
            {t('marktplaatsView.status.notPublished')}
          </Typography>
          {!listView && <div className="divider" />}
          <Button
            level="option"
            text={t('publish')}
            noPadding
            size="24"
            onClick={handlePublishCar}
          />
        </StyledMarktplaatsNotPublished>
      </StyledMarktplaatsCarCard>
    )
  }
}

MarktplaatsCarCard.propTypes = {
  className: propTypes.string,
  status: propTypes.string,
  iconOnly: propTypes.bool,
  onClick: propTypes.func,
  marktplaatsData: propTypes.object,
  carId: propTypes.string,
  onMarktplaatsStatusChange: propTypes.func,
  listView: propTypes.bool,
}

MarktplaatsCarCard.defaultProps = {
  className: '',
  status: CarFileMarktplaatsStatusEnum.NOT_PUBLISHED,
  iconOnly: false,
  onClick: null,
  marktplaatsData: null,
  carId: null,
  onMarktplaatsStatusChange: null,
  listView: false,
}

export default MarktplaatsCarCard
